/**
 * AnomalyChip — one detected anomaly (dip / spike / idle / weather-divergence).
 *
 * V3 Page 01 — compact, dismissible, clickable. Clicking opens the
 * ExplainPanel (via `onExplain`). Clicking the × dismisses the chip and
 * persists the hash in localStorage (`butler-dashboard-dismissed-anomalies`).
 *
 * Kind → tone mapping:
 *   revenue_dip       → watch tone (amber)
 *   attraction_idle   → watch tone (amber)
 *   scan_spike        → positive tone (butler blue)
 *   weather_divergence → informational tone (ink)
 */

import { X } from 'lucide-react'

export type AnomalyKind =
  | 'revenue_dip'
  | 'scan_spike'
  | 'attraction_idle'
  | 'weather_divergence'

export interface Anomaly {
  kind: AnomalyKind | string
  hour: string | null
  value_cents: number
  neighbor_avg_cents: number
  ratio: number
  label: string
  attraction_id?: number | null
  attraction_name?: string | null
}

export interface AnomalyChipProps {
  anomaly: Anomaly
  onExplain?: (anomaly: Anomaly) => void
  onDismiss?: (anomaly: Anomaly) => void
  'data-testid'?: string
}

function toneFor(kind: string): string {
  if (kind === 'revenue_dip' || kind === 'attraction_idle') {
    return 'border-glow-300 bg-glow-50 text-butler-900 dark:border-glow-700 dark:bg-glow-900/40 dark:text-glow-100'
  }
  if (kind === 'scan_spike') {
    return 'border-midway-300 bg-midway-50 text-midway-800 dark:border-midway-700 dark:bg-midway-900/40 dark:text-midway-100'
  }
  return 'border-hairline bg-white text-ink-soft dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300'
}

export function AnomalyChip({ anomaly, onExplain, onDismiss, ...rest }: AnomalyChipProps) {
  const testId = rest['data-testid'] || `anomaly-chip-${anomaly.kind}`
  const tone = toneFor(String(anomaly.kind))
  return (
    <div
      data-testid={testId}
      className={[
        'inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs',
        'shadow-card-soft transition-colors',
        tone,
      ].join(' ')}
      role="group"
      aria-label={anomaly.label}
    >
      <button
        type="button"
        className="font-semibold text-left max-w-[22rem] truncate focus:outline-none focus-visible:ring-2 focus-visible:ring-butler-400 rounded"
        onClick={() => onExplain?.(anomaly)}
        aria-label={`Explain: ${anomaly.label}`}
      >
        {anomaly.label}
      </button>
      <button
        type="button"
        className="text-ink-soft hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-butler-400 rounded-full"
        onClick={() => onDismiss?.(anomaly)}
        aria-label={`Dismiss anomaly: ${anomaly.label}`}
        data-testid={`${testId}-dismiss`}
      >
        <X className="h-3 w-3" aria-hidden="true" />
      </button>
    </div>
  )
}
